import { useState } from 'react';
import type { ReactNode } from 'react';

export type BannerTone = 'info' | 'success' | 'warning' | 'danger';

const ICON: Record<BannerTone, string> = {
  info: 'bx-info-circle',
  success: 'bx-check-circle',
  warning: 'bx-error',
  danger: 'bx-error-circle',
};

/**
 * Kridt's Banner (components.md, "Banner") — spec 248, review P1-4.
 *
 * A notice about the page or the data on it: paper, a hairline, the tone on a
 * 3px left edge and in the icon. It replaces filled amber panels — "AI engine
 * collection is degraded" was a brown slab in dark mode.
 *
 * Kridt's rules, carried here so a caller cannot get them wrong:
 * - One bold line, one plain line. If the notice names a fix, it links to it —
 *   pass the link inside `children`.
 * - Dismissing hides it for the SESSION, not forever: warning and danger stay
 *   until their cause is gone. Pass `dismissKey` to allow it.
 */
export function Banner({
  tone = 'info',
  title,
  children,
  dismissKey,
  onDismiss,
  testId,
  className,
}: {
  tone?: BannerTone;
  title: ReactNode;
  children?: ReactNode;
  /** A session-storage key. When set, a round Dismiss button hides the banner for this session. */
  dismissKey?: string;
  /** Called after dismissing — for a caller that also tracks the dismissal itself. */
  onDismiss?: () => void;
  testId?: string;
  className?: string;
}) {
  const storageKey = dismissKey ? `hub-banner-dismissed:${dismissKey}` : null;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (!storageKey) return false;
    try { return sessionStorage.getItem(storageKey) === '1'; } catch { return false; }
  });
  if (dismissed) return null;

  return (
    <div
      className={`hub-banner${className ? ' ' + className : ''}`}
      data-tone={tone}
      // A danger notice interrupts; everything else is announced politely.
      role={tone === 'danger' ? 'alert' : 'status'}
      data-testid={testId}
    >
      <i className={`bx ${ICON[tone]} hub-banner__icon`} aria-hidden="true" />
      <div className="hub-banner__body">
        <p className="hub-banner__title">{title}</p>
        {children && <div className="hub-banner__text">{children}</div>}
      </div>
      {storageKey && (
        <button
          type="button"
          className="hub-round hub-banner__dismiss"
          aria-label="Dismiss"
          data-testid={testId ? `${testId}-dismiss` : undefined}
          onClick={() => {
            try { sessionStorage.setItem(storageKey, '1'); } catch { /* private mode: dismiss for this render only */ }
            setDismissed(true);
            onDismiss?.();
          }}
        >
          <i className="bx bx-x" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
